import type { Configurator } from "./schema.js";

export type View = "exterior" | "interior";
export interface Selection {
  paint: string;
  wheels: string;
  trim: string;
}
export type OptionKind = keyof Selection;

export const OPTION_LABEL: Record<OptionKind, string> = { paint: "Paint", wheels: "Wheels", trim: "Interior trim" };

export function defaultSelection(c: Configurator): Selection {
  return { ...c.defaults };
}

/** Keep only valid option ids from `partial`; anything missing or unknown falls back to the default. */
export function normalizeSelection(c: Configurator, partial: Partial<Record<OptionKind, unknown>> | null | undefined): Selection {
  const d = defaultSelection(c);
  const pick = (list: { id: string }[], v: unknown, fallback: string) => (typeof v === "string" && list.some((o) => o.id === v) ? v : fallback);
  return { paint: pick(c.paints, partial?.paint, d.paint), wheels: pick(c.wheels, partial?.wheels, d.wheels), trim: pick(c.trims, partial?.trim, d.trim) };
}

export function withOption(c: Configurator, s: Selection, kind: OptionKind, id: string): Selection {
  return normalizeSelection(c, { ...s, [kind]: id });
}

export function exteriorPath(c: Configurator, s: Selection): string {
  return `${c.images.exterior.replace("{paint}", s.paint).replace("{wheels}", s.wheels)}.${c.images.extension}`;
}

export function interiorPath(c: Configurator, s: Selection): string {
  return `${c.images.interior.replace("{trim}", s.trim)}.${c.images.extension}`;
}

export function imagePath(c: Configurator, s: Selection, view: View): string {
  return view === "exterior" ? exteriorPath(c, s) : interiorPath(c, s);
}

/** Every image the configurator can show (for lint and shot lists): paints x wheels, plus one per trim. */
export function allImagePaths(c: Configurator): string[] {
  const out: string[] = [];
  for (const p of c.paints) for (const w of c.wheels) out.push(exteriorPath(c, { ...defaultSelection(c), paint: p.id, wheels: w.id }));
  for (const t of c.trims) out.push(interiorPath(c, { ...defaultSelection(c), trim: t.id }));
  return out;
}

/** Images worth preloading after the selected one: one option change away in the current view. */
export function neighbourPaths(c: Configurator, s: Selection, view: View): string[] {
  if (view === "interior") return c.trims.filter((t) => t.id !== s.trim).map((t) => interiorPath(c, { ...s, trim: t.id }));
  return [
    ...c.paints.filter((p) => p.id !== s.paint).map((p) => exteriorPath(c, { ...s, paint: p.id })),
    ...c.wheels.filter((w) => w.id !== s.wheels).map((w) => exteriorPath(c, { ...s, wheels: w.id }))
  ];
}

export interface Description {
  paint: string;
  wheels: string;
  trim: string;
  /** "Papaya Orange · Five-spoke silver · Black leather" */
  summary: string;
}

export function describeSelection(c: Configurator, s: Selection): Description {
  const name = (list: { id: string; name: string }[], id: string) => list.find((o) => o.id === id)?.name ?? id;
  const paint = name(c.paints, s.paint);
  const wheels = name(c.wheels, s.wheels);
  const trim = name(c.trims, s.trim);
  return { paint, wheels, trim, summary: `${paint} · ${wheels} wheels · ${trim} interior` };
}

export interface ConfiguredLine {
  productId: string;
  colour: string;
  size: string;
  detail: string;
  image: string;
}

/** The basket line for the current configuration (price on request: the product carries no price). */
export function configuredLine(c: Configurator, s: Selection): ConfiguredLine {
  const d = describeSelection(c, s);
  return { productId: c.productId, colour: d.paint, size: c.bodyLabel, detail: `${d.wheels} wheels · ${d.trim} interior`, image: exteriorPath(c, s) };
}

const STORAGE_KEY = "red.configurator.v1";

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function saveSelection(store: KeyValueStore | undefined, c: Configurator, s: Selection): void {
  try {
    store?.setItem(`${STORAGE_KEY}.${c.id}`, JSON.stringify(s));
  } catch {
    /* storage may be full or blocked: persistence is optional */
  }
}

export function loadSelection(store: KeyValueStore | undefined, c: Configurator): Selection {
  try {
    const raw = store?.getItem(`${STORAGE_KEY}.${c.id}`);
    if (!raw) return defaultSelection(c);
    const parsed: unknown = JSON.parse(raw);
    return normalizeSelection(c, typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null);
  } catch {
    return defaultSelection(c);
  }
}
